import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export function exportToCSV(data, columns, filename) {
  const header = columns.map(c => c.label).join(',');
  const rows = data.map(row => columns.map(c => {
    const val = typeof c.key === 'function' ? c.key(row) : row[c.key];
    // Escape commas and quotes
    const str = String(val ?? '');
    return str.includes(',') || str.includes('"') ? `"${str.replace(/"/g, '""')}"` : str;
  }).join(','));
  const csv = [header, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${filename}.csv`; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

export function generateStockReport(quote, overview, financials, currency = '₹') {
  const doc = new jsPDF();
  const sym = quote?.symbol || 'STOCK';
  const name = quote?.name || sym;

  // Header
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text(name, 14, 20);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(sym, 14, 28);

  // Price
  doc.setFontSize(16);
  doc.text(`${currency}${quote?.price?.toFixed(2) || 'N/A'}`, 14, 40);
  const change = quote?.change_percent ?? 0;
  doc.setTextColor(change >= 0 ? 0 : 255, change >= 0 ? 128 : 0, 0);
  doc.text(`${change >= 0 ? '+' : ''}${change.toFixed(2)}%`, 80, 40);
  doc.setTextColor(0, 0, 0);

  // Overview table
  doc.setFontSize(14);
  doc.text('Company Overview', 14, 55);
  autoTable(doc, {
    startY: 60,
    head: [['Metric', 'Value']],
    body: [
      ['Sector', overview?.sector || 'N/A'],
      ['Industry', overview?.industry || 'N/A'],
      ['Market Cap', overview?.market_cap ? `${currency}${(overview.market_cap / 1e7).toFixed(0)} Cr` : 'N/A'],
      ['52W High', overview?.fifty_two_week_high ? `${currency}${overview.fifty_two_week_high.toFixed(2)}` : 'N/A'],
      ['52W Low', overview?.fifty_two_week_low ? `${currency}${overview.fifty_two_week_low.toFixed(2)}` : 'N/A'],
      ['Employees', overview?.employees?.toLocaleString() || 'N/A'],
    ],
    theme: 'grid',
    styles: { fontSize: 10 },
  });

  // Financials table
  const y = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(14);
  doc.text('Key Financials', 14, y);
  autoTable(doc, {
    startY: y + 5,
    head: [['Metric', 'Value']],
    body: [
      ['P/E Ratio', financials?.pe_ratio?.toFixed(2) || 'N/A'],
      ['EPS', financials?.eps?.toFixed(2) || 'N/A'],
      ['ROE', financials?.return_on_equity ? `${(financials.return_on_equity * 100).toFixed(1)}%` : 'N/A'],
      ['Profit Margin', financials?.profit_margin ? `${(financials.profit_margin * 100).toFixed(1)}%` : 'N/A'],
      ['Debt/Equity', financials?.debt_to_equity?.toFixed(2) || 'N/A'],
      ['Dividend Yield', financials?.dividend_yield ? `${financials.dividend_yield.toFixed(2)}%` : 'N/A'],
    ],
    theme: 'grid',
    styles: { fontSize: 10 },
  });

  // Footer
  const pageHeight = doc.internal.pageSize.height;
  doc.setFontSize(8);
  doc.setTextColor(128, 128, 128);
  doc.text(`Generated by StockPulse on ${new Date().toLocaleDateString()}`, 14, pageHeight - 10);
  doc.text('For informational purposes only. Not financial advice.', 14, pageHeight - 5);

  doc.save(`${sym}_report.pdf`);
}

export function exportPortfolio(holdings, liveData, currency = '₹') {
  const columns = [
    { label: 'Symbol', key: 'symbol' },
    { label: 'Name', key: 'name' },
    { label: 'Quantity', key: 'quantity' },
    { label: 'Buy Price', key: 'buyPrice' },
    { label: 'Buy Date', key: 'buyDate' },
    { label: 'Current Price', key: row => liveData[row.symbol]?.price?.toFixed(2) || 'N/A' },
    { label: 'P&L', key: row => {
      const cp = liveData[row.symbol]?.price || row.buyPrice;
      return ((cp - row.buyPrice) * row.quantity).toFixed(2);
    }},
  ];
  exportToCSV(holdings, columns, `portfolio_${new Date().toISOString().split('T')[0]}`);
}
